import React, { useState, useEffect, useCallback } from 'react';
import { apiService } from '../../services/api';

interface FamilyMember {
  id: string;
  name: string;
  relationship: string;
  birthdate: string;
  color: string;
  avatar_emoji: string;
  notes: string;
}

interface WishlistItem {
  id: string;
  item_name: string;
  description: string;
  price: number;
  link: string;
  priority: number;
  occasion: string;
  is_purchased: boolean;
}

interface ClothingSize {
  id: string;
  item_type: string;
  size: string;
  brand: string;
}

const FamilyHub: React.FC = () => {
  const [members, setMembers] = useState<FamilyMember[]>([]);
  const [selectedMember, setSelectedMember] = useState<FamilyMember | null>(null);
  const [wishlist, setWishlist] = useState<WishlistItem[]>([]);
  const [sizes, setSizes] = useState<ClothingSize[]>([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState<'wishlist' | 'sizes'>('wishlist');
  const [showAddMemberModal, setShowAddMemberModal] = useState(false);
  const [showAddWishlistModal, setShowAddWishlistModal] = useState(false);
  const [showAddSizeModal, setShowAddSizeModal] = useState(false);
  const [newMember, setNewMember] = useState({ name: '', relationship: '', birthdate: '', color: '#667eea', avatarEmoji: '👤' });
  const [newWishlistItem, setNewWishlistItem] = useState({ itemName: '', description: '', price: 0, link: '', priority: 2, occasion: '' });
  const [newSize, setNewSize] = useState({ itemType: '', size: '', brand: '' });

  const fetchMembers = useCallback(async () => {
    try {
      setLoading(true);
      const response = await apiService.getFamilyMembers();
      if (response.success) {
        setMembers(response.members || []);
        if (response.members?.length > 0 && !selectedMember) {
          setSelectedMember(response.members[0]);
        }
      }
    } catch (error) {
      console.error('Failed to fetch family members:', error);
    } finally {
      setLoading(false);
    }
  }, [selectedMember]);

  const fetchMemberDetails = useCallback(async () => {
    if (!selectedMember) return;
    try {
      const [wishRes, sizesRes] = await Promise.all([
        apiService.getMemberWishlist(selectedMember.id),
        apiService.getMemberSizes(selectedMember.id)
      ]);
      if (wishRes.success) setWishlist(wishRes.wishlist || []);
      if (sizesRes.success) setSizes(sizesRes.sizes || []);
    } catch (error) {
      console.error('Failed to fetch member details:', error);
    }
  }, [selectedMember]);

  useEffect(() => { fetchMembers(); }, []);
  useEffect(() => { fetchMemberDetails(); }, [selectedMember, fetchMemberDetails]);

  const handleAddMember = async () => {
    if (!newMember.name.trim()) return;
    try {
      await apiService.createFamilyMember(newMember);
      setNewMember({ name: '', relationship: '', birthdate: '', color: '#667eea', avatarEmoji: '👤' });
      setShowAddMemberModal(false);
      fetchMembers();
    } catch (error) {
      console.error('Failed to add member:', error);
    }
  };

  const handleAddWishlistItem = async () => {
    if (!selectedMember || !newWishlistItem.itemName.trim()) return;
    try {
      await apiService.addWishlistItem(selectedMember.id, newWishlistItem);
      setNewWishlistItem({ itemName: '', description: '', price: 0, link: '', priority: 2, occasion: '' });
      setShowAddWishlistModal(false);
      fetchMemberDetails();
    } catch (error) {
      console.error('Failed to add wishlist item:', error);
    }
  };

  const handleAddSize = async () => {
    if (!selectedMember || !newSize.itemType.trim() || !newSize.size.trim()) return;
    try {
      await apiService.addMemberSize(selectedMember.id, newSize);
      setNewSize({ itemType: '', size: '', brand: '' });
      setShowAddSizeModal(false);
      fetchMemberDetails();
    } catch (error) {
      console.error('Failed to add size:', error);
    }
  };

  const handleTogglePurchased = async (itemId: string) => {
    try {
      await apiService.toggleWishlistPurchased(itemId);
      fetchMemberDetails();
    } catch (error) {
      console.error('Failed to toggle purchased:', error);
    }
  };

  const relationships = ['child', 'spouse', 'parent', 'sibling', 'grandparent', 'grandchild', 'friend', 'other'];
  const emojis = ['👤', '👦', '👧', '👨', '👩', '👴', '👵', '🧒', '👶', '🐕', '🐱'];
  const clothingTypes = ['Shirt', 'Pants', 'Dress', 'Shoes', 'Jacket', 'Hat', 'Socks', 'Underwear'];
  const occasions = ['Birthday', 'Christmas', 'Anniversary', 'Graduation', 'Just Because'];

  return (
    <div style={{ padding: '24px' }} data-testid="family-hub">
      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '24px' }}>
        <div>
          <h2 style={{ margin: 0, fontSize: '1.5rem', fontWeight: 700, color: '#1f2937' }}>Family Hub</h2>
          <p style={{ margin: '4px 0 0 0', color: '#6b7280', fontSize: '0.9rem' }}>Track wishlists, sizes, and important dates</p>
        </div>
        <button
          onClick={() => setShowAddMemberModal(true)}
          data-testid="add-member-btn"
          style={{ padding: '10px 20px', backgroundColor: '#8b5cf6', color: 'white', border: 'none', borderRadius: '8px', fontWeight: 600, cursor: 'pointer' }}
        >
          + Add Member
        </button>
      </div>

      {/* Member Avatars */}
      {members.length > 0 && (
        <div style={{ display: 'flex', gap: '16px', marginBottom: '24px', overflowX: 'auto', paddingBottom: '8px' }}>
          {members.map(member => (
            <button
              key={member.id}
              onClick={() => setSelectedMember(member)}
              style={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                padding: '12px 20px',
                backgroundColor: selectedMember?.id === member.id ? member.color : 'white',
                border: selectedMember?.id === member.id ? 'none' : '2px solid #e5e7eb',
                borderRadius: '12px',
                cursor: 'pointer',
                minWidth: '80px',
                transition: 'all 0.2s'
              }}
            >
              <span style={{ fontSize: '2rem' }}>{member.avatar_emoji}</span>
              <span style={{
                fontSize: '0.85rem',
                fontWeight: 600,
                marginTop: '4px',
                color: selectedMember?.id === member.id ? 'white' : '#374151'
              }}>
                {member.name}
              </span>
            </button>
          ))}
        </div>
      )}

      {/* Selected Member Details */}
      {loading ? (
        <div style={{ textAlign: 'center', padding: '40px' }}>Loading...</div>
      ) : !selectedMember ? (
        <div style={{ textAlign: 'center', padding: '60px', backgroundColor: '#f9fafb', borderRadius: '12px' }}>
          <p style={{ color: '#6b7280', marginBottom: '16px' }}>Add family members to track their wishlists and sizes</p>
          <button
            onClick={() => setShowAddMemberModal(true)}
            style={{ padding: '12px 24px', backgroundColor: '#8b5cf6', color: 'white', border: 'none', borderRadius: '8px', fontWeight: 600, cursor: 'pointer' }}
          >
            Add Your First Member
          </button>
        </div>
      ) : (
        <div>
          {/* Tabs */}
          <div style={{ display: 'flex', gap: '8px', marginBottom: '20px' }}>
            <button
              onClick={() => setActiveTab('wishlist')}
              style={{
                padding: '10px 20px',
                backgroundColor: activeTab === 'wishlist' ? '#8b5cf6' : 'white',
                color: activeTab === 'wishlist' ? 'white' : '#374151',
                border: activeTab === 'wishlist' ? 'none' : '1px solid #d1d5db',
                borderRadius: '8px',
                fontWeight: 600,
                cursor: 'pointer'
              }}
            >
              Wishlist ({wishlist.length})
            </button>
            <button
              onClick={() => setActiveTab('sizes')}
              style={{
                padding: '10px 20px',
                backgroundColor: activeTab === 'sizes' ? '#8b5cf6' : 'white',
                color: activeTab === 'sizes' ? 'white' : '#374151',
                border: activeTab === 'sizes' ? 'none' : '1px solid #d1d5db',
                borderRadius: '8px',
                fontWeight: 600,
                cursor: 'pointer'
              }}
            >
              Sizes ({sizes.length})
            </button>
          </div>

          {/* Wishlist Tab */}
          {activeTab === 'wishlist' && (
            <div>
              <button
                onClick={() => setShowAddWishlistModal(true)}
                style={{ width: '100%', padding: '12px', backgroundColor: '#f9fafb', border: '2px dashed #d1d5db', borderRadius: '8px', color: '#6b7280', cursor: 'pointer', marginBottom: '16px' }}
              >
                + Add Gift Idea
              </button>
              {wishlist.length === 0 ? (
                <p style={{ textAlign: 'center', color: '#6b7280' }}>No wishlist items yet</p>
              ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                  {wishlist.map(item => (
                    <div
                      key={item.id}
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        padding: '16px',
                        backgroundColor: item.is_purchased ? '#f0fdf4' : 'white',
                        borderRadius: '12px',
                        border: '1px solid #e5e7eb',
                        gap: '12px'
                      }}
                    >
                      <input
                        type="checkbox"
                        checked={item.is_purchased}
                        onChange={() => handleTogglePurchased(item.id)}
                        style={{ width: '20px', height: '20px' }}
                      />
                      <div style={{ flex: 1 }}>
                        <h4 style={{ margin: 0, textDecoration: item.is_purchased ? 'line-through' : 'none', color: item.is_purchased ? '#9ca3af' : '#1f2937' }}>
                          {item.item_name}
                        </h4>
                        {item.occasion && <span style={{ fontSize: '0.75rem', color: '#8b5cf6' }}>{item.occasion}</span>}
                      </div>
                      {item.price > 0 && (
                        <span style={{ fontWeight: 600, color: '#10b981' }}>${item.price}</span>
                      )}
                      {item.link && (
                        <a href={item.link} target="_blank" rel="noopener noreferrer" style={{ color: '#3b82f6', fontSize: '0.85rem' }}>View</a>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* Sizes Tab */}
          {activeTab === 'sizes' && (
            <div>
              <button
                onClick={() => setShowAddSizeModal(true)}
                style={{ width: '100%', padding: '12px', backgroundColor: '#f9fafb', border: '2px dashed #d1d5db', borderRadius: '8px', color: '#6b7280', cursor: 'pointer', marginBottom: '16px' }}
              >
                + Add Size
              </button>
              {sizes.length === 0 ? (
                <p style={{ textAlign: 'center', color: '#6b7280' }}>No sizes recorded yet</p>
              ) : (
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(150px, 1fr))', gap: '12px' }}>
                  {sizes.map(size => (
                    <div
                      key={size.id}
                      style={{ padding: '16px', backgroundColor: '#f9fafb', borderRadius: '12px', textAlign: 'center' }}
                    >
                      <p style={{ margin: 0, fontSize: '0.85rem', color: '#6b7280' }}>{size.item_type}</p>
                      <p style={{ margin: '4px 0', fontSize: '1.25rem', fontWeight: 700, color: '#1f2937' }}>{size.size}</p>
                      {size.brand && <p style={{ margin: 0, fontSize: '0.75rem', color: '#9ca3af' }}>{size.brand}</p>}
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {/* Add Member Modal */}
      {showAddMemberModal && (
        <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}>
          <div style={{ backgroundColor: 'white', borderRadius: '16px', padding: '24px', width: '90%', maxWidth: '400px' }}>
            <h3 style={{ margin: '0 0 16px 0' }}>Add Family Member</h3>
            <input
              type="text"
              value={newMember.name}
              onChange={(e) => setNewMember({ ...newMember, name: e.target.value })}
              placeholder="Name"
              style={{ width: '100%', padding: '12px', border: '1px solid #d1d5db', borderRadius: '8px', marginBottom: '12px', boxSizing: 'border-box' }}
            />
            <select
              value={newMember.relationship}
              onChange={(e) => setNewMember({ ...newMember, relationship: e.target.value })}
              style={{ width: '100%', padding: '12px', border: '1px solid #d1d5db', borderRadius: '8px', marginBottom: '12px' }}
            >
              <option value="">Relationship</option>
              {relationships.map(r => <option key={r} value={r}>{r.charAt(0).toUpperCase() + r.slice(1)}</option>)}
            </select>
            <div style={{ marginBottom: '12px' }}>
              <label style={{ display: 'block', marginBottom: '6px', fontSize: '0.85rem', color: '#6b7280' }}>Avatar</label>
              <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                {emojis.map(emoji => (
                  <button
                    key={emoji}
                    type="button"
                    onClick={() => setNewMember({ ...newMember, avatarEmoji: emoji })}
                    style={{
                      width: '40px', height: '40px',
                      border: newMember.avatarEmoji === emoji ? '2px solid #8b5cf6' : '1px solid #d1d5db',
                      borderRadius: '8px',
                      backgroundColor: 'white',
                      cursor: 'pointer',
                      fontSize: '1.25rem'
                    }}
                  >
                    {emoji}
                  </button>
                ))}
              </div>
            </div>
            <div style={{ display: 'flex', gap: '12px' }}>
              <button onClick={() => setShowAddMemberModal(false)} style={{ flex: 1, padding: '12px', border: '1px solid #d1d5db', borderRadius: '8px', backgroundColor: 'white', cursor: 'pointer' }}>Cancel</button>
              <button onClick={handleAddMember} style={{ flex: 1, padding: '12px', border: 'none', borderRadius: '8px', backgroundColor: '#8b5cf6', color: 'white', cursor: 'pointer', fontWeight: 600 }}>Add</button>
            </div>
          </div>
        </div>
      )}

      {/* Add Wishlist Modal */}
      {showAddWishlistModal && (
        <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}>
          <div style={{ backgroundColor: 'white', borderRadius: '16px', padding: '24px', width: '90%', maxWidth: '400px' }}>
            <h3 style={{ margin: '0 0 16px 0' }}>Add Gift Idea for {selectedMember?.name}</h3>
            <input
              type="text"
              value={newWishlistItem.itemName}
              onChange={(e) => setNewWishlistItem({ ...newWishlistItem, itemName: e.target.value })}
              placeholder="Item name"
              style={{ width: '100%', padding: '12px', border: '1px solid #d1d5db', borderRadius: '8px', marginBottom: '12px', boxSizing: 'border-box' }}
            />
            <input
              type="number"
              value={newWishlistItem.price || ''}
              onChange={(e) => setNewWishlistItem({ ...newWishlistItem, price: parseFloat(e.target.value) || 0 })}
              placeholder="Price (optional)"
              style={{ width: '100%', padding: '12px', border: '1px solid #d1d5db', borderRadius: '8px', marginBottom: '12px', boxSizing: 'border-box' }}
            />
            <input
              type="url"
              value={newWishlistItem.link}
              onChange={(e) => setNewWishlistItem({ ...newWishlistItem, link: e.target.value })}
              placeholder="Link (optional)"
              style={{ width: '100%', padding: '12px', border: '1px solid #d1d5db', borderRadius: '8px', marginBottom: '12px', boxSizing: 'border-box' }}
            />
            <select
              value={newWishlistItem.occasion}
              onChange={(e) => setNewWishlistItem({ ...newWishlistItem, occasion: e.target.value })}
              style={{ width: '100%', padding: '12px', border: '1px solid #d1d5db', borderRadius: '8px', marginBottom: '16px' }}
            >
              <option value="">Occasion (optional)</option>
              {occasions.map(o => <option key={o} value={o}>{o}</option>)}
            </select>
            <div style={{ display: 'flex', gap: '12px' }}>
              <button onClick={() => setShowAddWishlistModal(false)} style={{ flex: 1, padding: '12px', border: '1px solid #d1d5db', borderRadius: '8px', backgroundColor: 'white', cursor: 'pointer' }}>Cancel</button>
              <button onClick={handleAddWishlistItem} style={{ flex: 1, padding: '12px', border: 'none', borderRadius: '8px', backgroundColor: '#8b5cf6', color: 'white', cursor: 'pointer', fontWeight: 600 }}>Add</button>
            </div>
          </div>
        </div>
      )}

      {/* Add Size Modal */}
      {showAddSizeModal && (
        <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}>
          <div style={{ backgroundColor: 'white', borderRadius: '16px', padding: '24px', width: '90%', maxWidth: '400px' }}>
            <h3 style={{ margin: '0 0 16px 0' }}>Add Size for {selectedMember?.name}</h3>
            <select
              value={newSize.itemType}
              onChange={(e) => setNewSize({ ...newSize, itemType: e.target.value })}
              style={{ width: '100%', padding: '12px', border: '1px solid #d1d5db', borderRadius: '8px', marginBottom: '12px' }}
            >
              <option value="">Clothing type</option>
              {clothingTypes.map(t => <option key={t} value={t}>{t}</option>)}
            </select>
            <input
              type="text"
              value={newSize.size}
              onChange={(e) => setNewSize({ ...newSize, size: e.target.value })}
              placeholder="Size (e.g., M, 32, 10.5)"
              style={{ width: '100%', padding: '12px', border: '1px solid #d1d5db', borderRadius: '8px', marginBottom: '12px', boxSizing: 'border-box' }}
            />
            <input
              type="text"
              value={newSize.brand}
              onChange={(e) => setNewSize({ ...newSize, brand: e.target.value })}
              placeholder="Brand (optional)"
              style={{ width: '100%', padding: '12px', border: '1px solid #d1d5db', borderRadius: '8px', marginBottom: '16px', boxSizing: 'border-box' }}
            />
            <div style={{ display: 'flex', gap: '12px' }}>
              <button onClick={() => setShowAddSizeModal(false)} style={{ flex: 1, padding: '12px', border: '1px solid #d1d5db', borderRadius: '8px', backgroundColor: 'white', cursor: 'pointer' }}>Cancel</button>
              <button onClick={handleAddSize} style={{ flex: 1, padding: '12px', border: 'none', borderRadius: '8px', backgroundColor: '#8b5cf6', color: 'white', cursor: 'pointer', fontWeight: 600 }}>Add</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default FamilyHub;
